import React, { useState, useRef, useEffect } from "react";
import "./Music.css";

import ReactPlayer from "react-player";

function Music() {
  const [tracks, setTracks] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isAutoplayEnabled, setIsAutoplayEnabled] = useState(() => {
    try {
      const stored = localStorage.getItem("autoplay");
      return stored != null ? JSON.parse(stored) : true;
    } catch (e) {
      return true;
    }
  });
  const [pendingPlay, setPendingPlay] = useState(false);
  const [shuffleMode, setShuffleMode] = useState(false);
  const [repeatMode, setRepeatMode] = useState(false);

  const playerRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Fisher-Yates shuffle helper
  const shuffle = (arr) => {
    const a = Array.isArray(arr) ? arr.slice() : [];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };

  useEffect(() => {
    const data = require("./SourceData-v2.json");
    const list = Array.isArray(data) ? data : data.SourceData || [];
    const randomList = shuffle(list);
    setTracks(randomList);
    setCurrentIndex(0);
  }, []);

  useEffect(() => {
    let unmuteTimeout;
    if (tracks && tracks.length > 0 && isAutoplayEnabled) {
      // Request play via pendingPlay and keep muted initially for autoplay reliability
      setIsMuted(true);
      setPendingPlay(true);
      unmuteTimeout = setTimeout(() => setIsMuted(false), 1500);
    }
    return () => {
      if (unmuteTimeout) clearTimeout(unmuteTimeout);
    };
  }, [tracks, isAutoplayEnabled]);

  const playTrackAt = (index) => {
    if (!tracks || index < 0 || index >= tracks.length) return;
    setCurrentIndex(index);
    // user-initiated selection — unmute and request play
    setIsMuted(false);
    setPendingPlay(true);
  };

  const playNext = () => {
    if (!tracks.length) return;
    if (repeatMode) {
      // re-request play for the same track
      setPendingPlay(true);
      setIsMuted(false);
      return;
    }
    if (shuffleMode) {
      if (tracks.length === 1) {
        setCurrentIndex(0);
        setPendingPlay(true);
        setIsMuted(false);
        return;
      }
      let next = Math.floor(Math.random() * tracks.length);
      while (next === currentIndex) {
        next = Math.floor(Math.random() * tracks.length);
      }
      setCurrentIndex(next);
      setPendingPlay(true);
      setIsMuted(false);
      return;
    }
    const nextIndex = (currentIndex + 1) % tracks.length;
    setCurrentIndex(nextIndex);
    setPendingPlay(true);
    setIsMuted(false);
  };

  const playPrev = () => {
    if (!tracks.length) return;
    const prev = (currentIndex - 1 + tracks.length) % tracks.length;
    setCurrentIndex(prev);
    setPendingPlay(true);
    setIsMuted(false);
  };

  const toggleShuffle = () => setShuffleMode((s) => !s);
  const toggleRepeat = () => setRepeatMode((r) => !r);
  const togglePlay = () => {
    if (isPlaying) {
      setIsPlaying(false);
      setPendingPlay(false);
      return;
    }

    // Request play and try internal.play() with handled promise to avoid uncaught AbortError
    setPendingPlay(true);
    try {
      const internal =
        playerRef.current &&
        (playerRef.current.getInternalPlayer
          ? playerRef.current.getInternalPlayer()
          : playerRef.current);
      if (internal && typeof internal.play === "function") {
        const res = internal.play();
        if (res && typeof res.then === "function") {
          res
            .then(() => {
              if (mountedRef && mountedRef.current) {
                setPendingPlay(false);
                setIsPlaying(true);
              }
            })
            .catch((err) => {
              if (
                err &&
                (err.name === "AbortError" ||
                  (err.message &&
                    err.message.includes("removed from the document")))
              ) {
                // ignore race
              } else {
                console.warn("Play failed:", err);
              }
              if (mountedRef && mountedRef.current) setPendingPlay(false);
            });
        } else {
          if (mountedRef && mountedRef.current) {
            setPendingPlay(false);
            setIsPlaying(true);
          }
        }
        return;
      }
    } catch (e) {
      // ignore and rely on pendingPlay to start onReady
    }
  };
  const toggleMute = () => setIsMuted((m) => !m);
  const toggleAutoplay = () =>
    setIsAutoplayEnabled((v) => {
      const nv = !v;
      try {
        localStorage.setItem("autoplay", JSON.stringify(nv));
      } catch (e) {}
      return nv;
    });

  const currentTrack = tracks[currentIndex] || {};
  console.log("Current Track:", currentTrack);

  // Retry attempts when a play is pending: try internal.play() periodically for a short time
  useEffect(() => {
    if (!pendingPlay) return;
    const currentTrackLocal =
      tracks && tracks.length ? tracks[currentIndex] : null;
    if (!currentTrackLocal || !currentTrackLocal.url) {
      setPendingPlay(false);
      return;
    }

    let attempts = 0;
    let interval = null;

    const tryPlay = async () => {
      attempts += 1;
      try {
        const internal =
          playerRef.current &&
          (playerRef.current.getInternalPlayer
            ? playerRef.current.getInternalPlayer()
            : playerRef.current);
        if (internal && typeof internal.play === "function") {
          const res = internal.play();
          if (res && typeof res.then === "function") await res;
          if (mountedRef && mountedRef.current) {
            setPendingPlay(false);
            setIsPlaying(true);
          }
          if (interval) clearInterval(interval);
          return;
        }
      } catch (err) {
        if (
          err &&
          (err.name === "AbortError" ||
            (err.message && err.message.includes("removed from the document")))
        ) {
          // expected race - ignore
        } else {
          console.warn("play attempt failed:", err);
        }
      }

      if (attempts >= 6) {
        if (mountedRef && mountedRef.current) setPendingPlay(false);
        if (interval) clearInterval(interval);
      }
    };

    tryPlay();
    interval = setInterval(tryPlay, 500);
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [pendingPlay, currentIndex, tracks]);
  return (
    <>
      <div className="container">
        <div className="music-header">
          <h1>A collection of sounds and stories by CooMBaaH and Friends </h1>
        </div>
        <div className="track_desp">
          {currentTrack.title || "No track selected"}
          {isPlaying && tracks.length > 0 && (
            <span
              style={{
                marginLeft: 8,
                padding: "2px 6px",
                background: "#4caf50",
                color: "#fff",
                borderRadius: 4,
                fontSize: 12,
              }}
            >
              Playing
            </span>
          )}
        </div>

        <section>
          <ReactPlayer
            className="react-player"
            src={currentTrack.url}
            playing={isPlaying}
            muted={isMuted}
            controls
            width="100%"
            height="360px"
            style={{ maxWidth: "900px", height: "360px", margin: "0 auto" }}
            ref={playerRef}
            config={{
              file: { attributes: { crossOrigin: "true" } },
              youtube: { color: "white" },
            }}
            onReady={() => {
              console.log("onReady fired");
              // If a play was requested before player was ready, try to play now
              if (pendingPlay) {
                try {
                  const internal =
                    playerRef.current &&
                    (playerRef.current.getInternalPlayer
                      ? playerRef.current.getInternalPlayer()
                      : playerRef.current);
                  if (internal && typeof internal.play === "function") {
                    const res = internal.play();
                    if (res && typeof res.then === "function") {
                      res
                        .then(() => {
                          if (mountedRef && mountedRef.current) {
                            setPendingPlay(false);
                            setIsPlaying(true);
                          }
                        })
                        .catch((err) => {
                          console.warn("play failed onReady:", err);
                          if (mountedRef && mountedRef.current)
                            setPendingPlay(false);
                        });
                    } else {
                      if (mountedRef && mountedRef.current) {
                        setPendingPlay(false);
                        setIsPlaying(true);
                      }
                    }
                  }
                } catch (e) {
                  console.warn("onReady play attempt failed:", e);
                  if (mountedRef && mountedRef.current) setPendingPlay(false);
                }
              }
            }}
            onPlay={() => {
              console.log("onPlay fired");
              console.log(playerRef.current);
              // playback has started
              setPendingPlay(false);
              setIsPlaying(true);
              if (isAutoplayEnabled && isMuted) {
                // Try to unmute automatically once playback has started and autoplay is allowed
                setIsMuted(false);
              }
            }}
            onPause={() => setIsPlaying(false)}
            onEnded={() => playNext()}
            onError={(err) => {
              console.error("ReactPlayer onError:", err);
              // stop retrying if there's a persistent error
              setPendingPlay(false);
            }}
          />

          {/* Debug: expose the current URL so it's easy to see what will be requested */}
          {/* <div style={{ fontSize: 12, color: "#666", marginTop: 8 }}>
            URL: {currentTrack.url || "(none)"}
          </div> */}
        </section>

        <div className="controls" style={{ marginTop: 10 }}>
          <button
            onClick={playPrev}
            aria-label="Previous track"
            title="Previous"
          >
            <svg
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path d="M6 6v12l10-6zM6 6h2v12H6z" />
            </svg>
            Prev
          </button>

          <button
            onClick={togglePlay}
            className={isPlaying ? "selected" : ""}
            aria-pressed={isPlaying}
            aria-label={isPlaying ? "Pause" : "Play"}
            title={isPlaying ? "Pause" : "Play"}
          >
            {isPlaying ? (
              <svg
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <rect x="6" y="5" width="4" height="14" />
                <rect x="14" y="5" width="4" height="14" />
              </svg>
            ) : (
              <svg
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
            {isPlaying ? "Pause" : "Play"}
          </button>

          <button onClick={playNext} aria-label="Next track" title="Next">
            <svg
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path d="M6 18V6l10 6-10 6zM18 6v12h-2V6h2z" />
            </svg>
            Next
          </button>

          <button
            onClick={toggleShuffle}
            className={shuffleMode ? "selected" : ""}
            style={{ fontWeight: shuffleMode ? "bold" : "normal" }}
            title="Shuffle"
          >
            <svg
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path d="M3 6h6l4 4 4-4h4v2h-3.17L13 13 9.17 9H3V6zm18 12h-6l-4-4-4 4H1v-2h3.17L11 11l3.83 4H21v3z" />
            </svg>
            Shuffle
          </button>

          <button
            onClick={toggleRepeat}
            className={repeatMode ? "selected" : ""}
            style={{ fontWeight: repeatMode ? "bold" : "normal" }}
            title="Repeat"
          >
            <svg
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path d="M7 7h10v4l4-4-4-4v4H7v2zm10 10H7v-4l-4 4 4 4v-4h10v-2z" />
            </svg>
            Repeat
          </button>

          <button onClick={toggleMute} className={isMuted ? "selected" : ""}>
            {isMuted ? "Unmute" : "Mute"}
          </button>
          <button
            onClick={toggleAutoplay}
            className={isAutoplayEnabled ? "selected" : ""}
            style={{ fontWeight: isAutoplayEnabled ? "bold" : "normal" }}
          >
            Autoplay: {isAutoplayEnabled ? "On" : "Off"}
          </button>

          {/* Compact track selector so the playlist isn't shown on-screen */}
          <label
            className="trackSelect"
            style={{
              margin: "12px auto",
              flexWrap: "wrap",
              display: "flex",
              flexBasis: "50%",
              padding: 4,
            }}
          >
            Track:
            <select
              value={currentIndex}
              onChange={(e) => playTrackAt(Number(e.target.value))}
              style={{
                margin: "auto",
                flexWrap: "wrap",
                display: "flex",
                alignItems: "center",
                flexBasis: "50%",
                padding: 2,
                marginLeft: 20,
              }}
            >
              {tracks.map((t, i) => (
                <option key={i} value={i}>
                  {t.title || `Track ${i + 1}`}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="track_desp" style={{ marginTop: 10 }}>
          {currentTrack.desript}
        </div>
      </div>
    </>
  );
}

export default Music;
